import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import {
  ROLE_ORG_MEMBER,
  ROLE_RISK_ANALYST,
} from "../../services/userService";
import {
  Search,
  Bell,
  LogOut,
  Shield,
  UserRound,
} from "lucide-react";
import FormControl from "../common/FormControl";
import "./Header.css";

interface HeaderProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
  onLogoClick?: () => void;
  onLayoutTestClick?: () => void;
  onLayoutTest2Click?: () => void;
}

// Role display labels — single source of truth for header UI
const ROLE_LABEL: Record<number, string> = {
  1: "GRC Admin",
  2: "Org Member",
  3: "Audit User",
  4: "Risk Analyst",
  5: "C-Suite",
  6: "Costing User",
};

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  sidebarOpen,
  onLogoClick,
}) => {
  const { state, logout } = useAuth();
  const { setRole } = useTheme();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Numeric role from auth state or per-tab sessionStorage fallback
  const userRoleStr =
    state.user?.role?.toString() || sessionStorage.getItem("anygrc_user_role");
  const userRole = userRoleStr ? parseInt(userRoleStr, 10) : null;

  // Sync [data-role] on <html> whenever the logged-in user's role changes.
  // This activates the matching brand token block in variables.css.
  useEffect(() => {
    setRole(userRole);
  }, [userRole, setRole]);

  const handleLogout = async () => {
    try {
      await logout();
      setRole(null);
      localStorage.removeItem('anygrc_role_theme');
      setUserMenuOpen(false);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);

  const handleUserMenuBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setUserMenuOpen(false);
    }
  };

  // Hide sidebar toggle for roles that don't have a sidebar
  const hideSidebarToggle =
    userRole === ROLE_ORG_MEMBER || userRole === ROLE_RISK_ANALYST;

  const roleLabel =
    userRole != null ? (ROLE_LABEL[userRole] ?? "AnyGRC") : "AnyGRC";

  return (
    <header className="header">
      <div className="header-left">
        {!hideSidebarToggle && (
          <button
            data-testid="BTN_header_toggle_sidebar"
            className="sidebar-toggle"
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
          >
            <span className={`hamburger ${sidebarOpen ? "open" : ""}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        )}

        <button
          data-testid="BTN_header_logo"
          className="logo"
          onClick={onLogoClick}
          aria-label="Go to Dashboard"
        >
          {/* Logo badge */}
          <div className="logo-badge">
            <img
              src={process.env.PUBLIC_URL + "/anygrc-logo.png"}
              alt="AnyGRC"
              className="logo-img"
            />
          </div>

          {/* Role icon — Lucide Shield, color from currentColor (white via .header) */}
          <Shield size={22} aria-hidden="true" />

          {/* Role label */}
          <span className="tagline">{roleLabel}</span>

          {/* Entity name separator */}
          {state.user?.entity_name && (
            <span className="entity-name">{state.user.entity_name}</span>
          )}
        </button>
      </div>

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      <div className="header-search-wrapper">
        <FormControl.Input
          data-testid="INP_header_search"
          className="header-search-input"
          placeholder="Search anything (Frameworks, Risks, Audits...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leadingIcon={<Search size={18} />}
          size="md"
        />
      </div>

      {/* ── Right section ──────────────────────────────────────────────────── */}
      <div className="header-right">
        <div className="notifications">
          <button
            data-testid="BTN_header_notifications"
            className="notification-btn"
            aria-label="Notifications (3 unread)"
          >
            <Bell size={18} aria-hidden="true" />
            <span className="notification-badge" aria-hidden="true">
              3
            </span>
          </button>
        </div>

        <div
          className="user-profile-section"
          onBlur={handleUserMenuBlur}
          tabIndex={-1}
        >
          <div className="user-info">
            <span className="user-name">
              {[state.user?.first_name, state.user?.last_name].filter(Boolean).join(' ') || state.user?.login_id || 'User'}
            </span>
            <span className="user-email">{state.user?.email || state.user?.login_id}</span>
            <span className="user-role-label">{roleLabel}</span>
          </div>

          <div
            data-testid="BTN_header_user_menu"
            className="user-avatar"
            onClick={toggleUserMenu}
            role="button"
            aria-label="User menu"
            tabIndex={0}
          >
            <UserRound size={20} aria-hidden="true" />
          </div>

          <button
            data-testid="BTN_header_logout"
            className="logout-btn"
            onClick={handleLogout}
            title="Sign Out"
            aria-label="Sign Out"
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
